/**
 * Weapon records (R13, R15).
 *
 * Range and speed are Q16.16 in cells; cooldown and travel are in ticks.
 * Everything is data — combat reads these, it does not special-case them.
 */

import * as fix from '../../core/fixed.js'
import { WARHEAD } from './armor.js'

/** How a weapon delivers damage. */
export const PROJECTILE = {
  /** Applies on the firing tick. Bullets, beams. */
  Instant: 0,
  /** Travels; can miss a target that dies en route. Shells, rockets. */
  Ballistic: 1,
  /** Continuous while in range. Applies every tick at a reduced rate. */
  Beam: 2,
} as const

export type ProjectileKind = (typeof PROJECTILE)[keyof typeof PROJECTILE]

export interface WeaponDef {
  readonly id: number
  readonly key: string
  readonly name: string
  readonly damage: number
  /** Q16.16 cells. */
  readonly range: fix.Fixed
  /** Ticks between shots. At 30 Hz, 30 is one shot per second. */
  readonly cooldown: number
  readonly warhead: number
  readonly projectile: ProjectileKind
  /** Q16.16 cells. Zero for a single-target weapon. */
  readonly splash: fix.Fixed
  /** Q16.16 cells per tick. Ignored for instant weapons. */
  readonly projectileSpeed: fix.Fixed
  /** BAM16 units per tick the turret can rotate. */
  readonly turnRate: number
}

function weapon(
  id: number,
  key: string,
  name: string,
  damage: number,
  rangeCells: number,
  cooldown: number,
  warhead: number,
  projectile: ProjectileKind,
  opts: { splash?: number; speed?: number; turnRate?: number } = {},
): WeaponDef {
  return {
    id,
    key,
    name,
    damage,
    range: fix.fromFloat(rangeCells),
    cooldown,
    warhead,
    projectile,
    splash: fix.fromFloat(opts.splash ?? 0),
    projectileSpeed: fix.fromFloat(opts.speed ?? 0.5),
    turnRate: opts.turnRate ?? 1500,
  }
}

export const WEAPONS: WeaponDef[] = [
  // --- Helios Directorate ---------------------------------------------------
  weapon(1, 'service-rifle', 'Service Rifle', 14, 4.0, 18, WARHEAD.SmallArms, PROJECTILE.Instant, {
    turnRate: 4000,
  }),
  weapon(2, 'buggy-autocannon', 'Autocannon', 11, 4.5, 10, WARHEAD.AutoCannon, PROJECTILE.Instant, {
    turnRate: 3000,
  }),
  weapon(3, 'guardian-cannon', 'Guardian Cannon', 42, 5.5, 42, WARHEAD.ApShell, PROJECTILE.Ballistic, {
    speed: 0.8,
    turnRate: 1200,
  }),
  weapon(4, 'turret-gun', 'Turret Gun', 26, 6.0, 24, WARHEAD.DefenseGun, PROJECTILE.Instant, {
    turnRate: 2200,
  }),
  weapon(5, 'mirror-lance', 'Orbital Mirror Lance', 260, 0, 0, WARHEAD.Orbital, PROJECTILE.Instant, {
    splash: 4.5,
  }),

  // --- The Ascendancy -------------------------------------------------------
  weapon(11, 'swarm-cutters', 'Swarm Cutters', 12, 3.5, 15, WARHEAD.SmallArms, PROJECTILE.Instant, {
    turnRate: 5000,
  }),
  weapon(12, 'skirmish-arc', 'Skirmish Arc', 13, 5.0, 14, WARHEAD.Arc, PROJECTILE.Instant, {
    turnRate: 3200,
  }),
  weapon(13, 'walker-mortar', 'Walker Mortar', 50, 6.5, 54, WARHEAD.Siege, PROJECTILE.Ballistic, {
    splash: 1.2,
    speed: 0.55,
    turnRate: 900,
  }),
  weapon(14, 'arc-pylon', 'Arc Pylon', 24, 6.0, 22, WARHEAD.DefenseGun, PROJECTILE.Instant, {
    turnRate: 2200,
  }),
  weapon(15, 'logic-bomb', 'Logic Bomb', 240, 0, 0, WARHEAD.Collapse, PROJECTILE.Instant, {
    splash: 5.0,
  }),

  // --- aircraft -------------------------------------------------------------
  // Three rounds per sortie, fired in quick succession, then home to rearm.
  // The magazine is the balance, not the cooldown: a short cooldown lets a
  // pass empty its rack on one target, and the trip back is the price.
  weapon(6, 'hornet-payload', 'Hornet Payload', 62, 4.5, 22, WARHEAD.HeShell, PROJECTILE.Ballistic, {
    splash: 1.8,
    speed: 1.1,
    turnRate: 6000,
  }),
  weapon(16, 'wraith-lance', 'Wraith Lance', 55, 5.2, 20, WARHEAD.Siege, PROJECTILE.Instant, {
    splash: 1.2,
    turnRate: 6000,
  }),

  // --- tier three -----------------------------------------------------------
  //
  // These are slow, expensive and specialised on purpose. A third tier that is
  // simply "the same units with bigger numbers" adds no decisions: it changes
  // when you win, not how. Each of these is better than a Guardian Tank at one
  // job and worse at everything else.

  // Helios. Long reach, hits hard, and fires slowly enough that being caught
  // between shots is a real risk — which is what the escort is for.
  weapon(7, 'prism-beam', 'Prism Beam', 52, 6.8, 52, WARHEAD.Beam, PROJECTILE.Instant, {
    turnRate: 1400,
  }),
  // Outranges every defence in the game, and cannot fire inside its own
  // minimum range. Siege that has to be defended, not siege that defends itself.
  weapon(8, 'siege-battery', 'Siege Battery', 74, 11.0, 88, WARHEAD.Bombard, PROJECTILE.Ballistic, {
    splash: 1.9,
    speed: 0.42,
    turnRate: 700,
  }),
  weapon(9, 'warden-cannon', 'Warden Cannon', 64, 5.0, 46, WARHEAD.ApShell, PROJECTILE.Ballistic, {
    speed: 0.85,
    turnRate: 1000,
  }),

  // The Ascendancy's beam pierces instead of chaining: the same tier, the same
  // counters, a different reason to position it.
  // Hits harder per shot than the Prism Beam, because its bonus is
  // situational and the Prism's is not: piercing only matters when the enemy
  // obliges by standing in a line.
  weapon(17, 'lance-arc', 'Lance Arc', 58, 6.8, 44, WARHEAD.Beam, PROJECTILE.Instant, {
    turnRate: 1600,
  }),
  weapon(18, 'void-mortar', 'Void Mortar', 68, 10.5, 84, WARHEAD.Bombard, PROJECTILE.Ballistic, {
    splash: 2.1,
    speed: 0.45,
    turnRate: 750,
  }),
  // Arc rather than siege, and reach enough to use it.
  //
  // As a short-ranged siege weapon this was the worst unit in the game and the
  // measurements said so: it had to close to 3.2 cells against a Warden firing
  // from 5.0, so it spent the approach being shot and never arrived, and its
  // warhead was weak against exactly the light armour and infantry that make up
  // most of an army. Two tier-three brawlers where one cannot reach the other
  // is not a matchup.
  //
  // It is now the anti-swarm answer to the Warden's anti-armour: better against
  // everything light, decidedly worse against heavy, and still the one that has
  // to walk in.
  // The chassis gun, used when an IFV is empty. Deliberately weak: an empty
  // IFV is a fast scout that can defend itself, and the whole point of the
  // unit is that it is worth putting somebody in.
  weapon(22, 'chassis-gun', 'Chassis Gun', 9, 4.0, 14, WARHEAD.SmallArms, PROJECTILE.Instant, {
    turnRate: 1500,
  }),
  // Shared by both factions' marksmen. Identical numbers on purpose: a
  // mirrored counter that is not actually mirrored is a faction advantage
  // wearing a different unit's name.
  weapon(20, 'marksman-rifle', 'Marksman Rifle', 95, 9.5, 62, WARHEAD.Marksman, PROJECTILE.Instant, {
    turnRate: 900,
  }),
  weapon(19, 'colossus-flail', 'Colossus Flail', 38, 4.4, 20, WARHEAD.Arc, PROJECTILE.Instant, {
    splash: 1.2,
    turnRate: 1100,
  }),

  // --- shared ---------------------------------------------------------------
  weapon(21, 'demolition-charge', 'Demolition Charge', 70, 1.5, 90, WARHEAD.HeShell, PROJECTILE.Instant, {
    splash: 1.0,
    turnRate: 4000,
  }),
]

const byId = new Map<number, WeaponDef>()
const byKey = new Map<string, WeaponDef>()
for (const w of WEAPONS) {
  byId.set(w.id, w)
  byKey.set(w.key, w)
}

/** Zero means unarmed, and is deliberately not a valid weapon id. */
export const NO_WEAPON = 0

export function weaponById(id: number): WeaponDef | undefined {
  return byId.get(id)
}

export function weaponByKey(key: string): WeaponDef | undefined {
  return byKey.get(key)
}
